// ============================================================
// Demo (showcase) mode — access code and the sample identities.
//
// Purpose: management needs to show anyone — a prospect, a partner, a new
// hire — what the product looks like from every side: Property Owner,
// Tenant, CEO and Airbnb Guest. A real login only ever shows one of those,
// and only if that person happens to own a unit.
//
// ON THE ACCESS CODE — read this before treating it as security:
// the code is compared IN THE BROWSER, so it ships inside the JavaScript
// bundle and anyone who opens devtools can read it. It is a speed bump that
// keeps casual visitors out of the showcase, NOT an access control.
//
// That is acceptable only because everything behind it is fabricated: the
// portals run with bundled sample fixtures and all API traffic is blocked
// (see demoNetwork.ts). If demo mode is ever changed to read real data, this
// gate must move to the server first.
// ============================================================

/**
 * Access code, injected at build time. Falls back to a fixed code so a local
 * dev build works without env setup.
 *
 * To change it: set REACT_APP_DEMO_CODE in the build environment and rebuild.
 * There is no way to rotate it without a rebuild — that is the trade-off of
 * checking client-side.
 */
export const DEMO_CODE = (process.env.REACT_APP_DEMO_CODE || 'INVESTMENTER').trim();

/**
 * FILM MODE — for recording product videos.
 *
 * A promo film cannot show the showcase scaffolding: the Guest Access row,
 * the access-code screen, the "choose a view" hub and the sample-data banner
 * would all be in frame, and none of them exist for a real customer. In film
 * mode the Property Owner and Tenant rows on the entry screen open their
 * portals directly on the sample portfolio, with the banners suppressed — so
 * what is recorded is exactly the live app's view, running on figures that
 * are safe to publish.
 *
 * GATED BY HOSTNAME, and this is the whole safety argument: it turns on only
 * on a Firebase preview channel whose name starts `video-demo`. The live
 * portal — portal.investmenter.de, investor-database-c0c49.web.app — and the
 * Play Store app (which runs on localhost/capacitor origins) cannot match
 * this pattern, so no real visitor can ever be shown fabricated holdings with
 * the sample-data warning removed. Do not widen this test to a query
 * parameter or a stored flag: a URL anyone can type would put a build with no
 * warning in front of real users, which is the one thing it must never do.
 *
 * Preview channel URLs look like:
 *   https://investor-database-c0c49--video-demo-<hash>.web.app
 */
export function isFilmMode(): boolean {
  try {
    return /--video-demo[-a-z0-9]*\.web\.app$/i.test(window.location.hostname);
  } catch {
    return false;
  }
}

/** Case- and whitespace-insensitive: staff read this code off a slide. */
export function isValidDemoCode(entered: string): boolean {
  return (entered || '').trim().toUpperCase() === DEMO_CODE.toUpperCase();
}

const UNLOCK_KEY = 'demoUnlocked';

/**
 * Unlock survives navigation between the four views but NOT a relaunch —
 * sessionStorage, so a device left unlocked on someone's desk does not stay
 * in demo mode forever.
 */
export function markDemoUnlocked(): void {
  try {
    sessionStorage.setItem(UNLOCK_KEY, '1');
  } catch {
    /* private mode — the in-memory state in RootRouter still carries it */
  }
}

export function isDemoUnlocked(): boolean {
  try {
    return sessionStorage.getItem(UNLOCK_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearDemoUnlock(): void {
  try {
    sessionStorage.removeItem(UNLOCK_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Whether the "Guest Access" row is offered on the welcome screen.
 *
 * On the live portal (portal.investmenter.com) the showcase is a staff tool,
 * not something an investor signing in should be presented with. So in a
 * production build the row appears only when deliberately asked for:
 *
 *   https://portal.investmenter.com/?demo=1        ← bookmark this
 *   https://portal.investmenter.com/#demo
 *
 * Local and preview builds always show it, so it stays easy to review.
 *
 * This is about keeping the investors' screen clean, not about secrecy — the
 * parameter is guessable and the access code is in the bundle either way.
 * What it does buy: nobody wanders into demo mode by accident on the real
 * portal, which also keeps the window.fetch patch away from real sessions.
 */
export function isDemoEntryVisible(): boolean {
  if (process.env.NODE_ENV !== 'production') return true;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('demo') === '1') return true;
    return window.location.hash.replace('#', '').toLowerCase() === 'demo';
  } catch {
    return false;
  }
}

/** The four views the showcase offers. */
export type DemoView = 'owner' | 'tenant' | 'ceo' | 'guest';

/**
 * The sample investor the Owner view runs as. `isDemo` drives the same
 * sample-data banner the real isDemo accounts get, so the demo and a real
 * sample account look consistent.
 *
 * The email is deliberately an obvious placeholder — nobody should be able to
 * mistake it for a real investor, and it must not collide with a real
 * investors doc id if it ever reaches a log.
 */
export const DEMO_INVESTOR = {
  id: 'sample.investor@example.com',
  name: 'Sample Investor',
  email: 'sample.investor@example.com',
  phone: '+971 50 000 0000',
  nationality: 'Germany',
  isDemo: true,
};
